import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export const ContactUs = () => {
  return <div className="min-h-screen bg-white" data-id="element-432">
      {/* Page Header */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden pt-24" data-id="element-433">
        <div className="absolute inset-0 z-0" data-id="element-434">
          <img src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&q=80&w=2000" alt="Contact Elisabeth House" className="w-full h-full object-cover" data-id="element-435" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-charcoal/20" data-id="element-436" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-437">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-438">
            <div className="flex items-center space-x-3 text-white/60 text-xs uppercase tracking-[0.3em] mb-8" data-id="element-439">
              <a href="/" className="hover:text-warmCoral transition-colors" data-id="element-440">
                Home
              </a>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-441" />
              <span className="text-white" data-id="element-442">Contact Us</span>
            </div>

            <div className="relative inline-block" data-id="element-443">
              <h1 className="text-white text-5xl md:text-7xl font-serif leading-none mb-4" data-id="element-444">
                Contact Us
              </h1>
              <motion.span initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="absolute -top-16 -right-8 text-warmCoral font-accent text-4xl md:text-5xl rotate-[-12deg] hidden md:block" data-id="element-445">
                Get in Touch
              </motion.span>
            </div>

            <p className="text-white/80 text-xl md:text-2xl max-w-2xl font-light leading-relaxed mt-8 border-l-2 border-warmCoral pl-8" data-id="element-446">
              We are here to answer your questions, provide guidance, and
              welcome you to our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 px-4 md:px-8 bg-white" data-id="element-447">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20" data-id="element-448">
          {/* Contact Info & Map */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-449">
            <span className="text-warmCoral tracking-[0.2em] uppercase text-sm font-medium mb-4 block" data-id="element-450">
              Reach Out
            </span>
            <h2 className="text-charcoal text-5xl font-serif mb-8" data-id="element-451">
              Begin Your Journey
            </h2>
            <p className="text-charcoal/60 text-xl mb-12 leading-relaxed" data-id="element-452">
              We invite you to experience the Elisabeth House difference in
              person. Contact our concierge team to schedule a private tour or
              discuss your family's unique needs.
            </p>

            <div className="grid sm:grid-cols-2 gap-8 mb-12" data-id="element-453">
              <div className="flex items-start space-x-4" data-id="element-454">
                <div className="bg-warmGray/10 p-4 rounded-2xl" data-id="element-455">
                  <MapPin className="text-warmCoral w-6 h-6" data-id="element-456" />
                </div>
                <div data-id="element-457">
                  <div className="text-charcoal/40 text-sm uppercase tracking-widest mb-1" data-id="element-458">
                    Address
                  </div>
                  <div className="text-charcoal text-lg font-serif" data-id="element-459">
                    12902 Luxury Lane
                    <br data-id="element-460" />
                    Hagerstown, MD 21740
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4" data-id="element-461">
                <div className="bg-warmGray/10 p-4 rounded-2xl" data-id="element-462">
                  <Phone className="text-warmCoral w-6 h-6" data-id="element-463" />
                </div>
                <div data-id="element-464">
                  <div className="text-charcoal/40 text-sm uppercase tracking-widest mb-1" data-id="element-465">
                    Phone
                  </div>
                  <div className="text-charcoal text-lg font-serif" data-id="element-466">
                    +1 (301) 555-0123
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4" data-id="element-467">
                <div className="bg-warmGray/10 p-4 rounded-2xl" data-id="element-468">
                  <Mail className="text-warmCoral w-6 h-6" data-id="element-469" />
                </div>
                <div data-id="element-470">
                  <div className="text-charcoal/40 text-sm uppercase tracking-widest mb-1" data-id="element-471">
                    Email
                  </div>
                  <div className="text-charcoal text-lg font-serif" data-id="element-472">
                    hello@elisabethhouse.com
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4" data-id="element-473">
                <div className="bg-warmGray/10 p-4 rounded-2xl" data-id="element-474">
                  <Clock className="text-warmCoral w-6 h-6" data-id="element-475" />
                </div>
                <div data-id="element-476">
                  <div className="text-charcoal/40 text-sm uppercase tracking-widest mb-1" data-id="element-477">
                    Hours
                  </div>
                  <div className="text-charcoal text-lg font-serif" data-id="element-478">
                    Mon-Fri: 8am - 6pm
                    <br data-id="element-479" />
                    Sat-Sun: 10am - 4pm
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[32px] overflow-hidden h-80 shadow-xl grayscale hover:grayscale-0 transition-all duration-700 border border-warmGray/20" data-id="element-480">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12345.6789!2d-77.719!3d39.641!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDM4JzI3LjYiTiA3N8KwNDMnMDguNCJX!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" title="Elisabeth House Location" data-id="element-481" />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-warmGray/5 p-10 md:p-14 rounded-[40px] border border-warmGray/10 h-fit" data-id="element-482">
            <h3 className="text-3xl font-serif text-charcoal mb-8" data-id="element-483">
              Send an Inquiry
            </h3>
            <form className="space-y-6" data-id="element-484">
              <div className="grid md:grid-cols-2 gap-6" data-id="element-485">
                <div className="space-y-2" data-id="element-486">
                  <label className="text-xs uppercase tracking-widest text-charcoal/40 font-medium" data-id="element-487">
                    First Name
                  </label>
                  <input type="text" className="w-full bg-white border border-warmGray/20 rounded-xl p-4 focus:ring-2 focus:ring-warmCoral/20 focus:border-warmCoral outline-none transition-all" placeholder="Jane" data-id="element-488" />
                </div>
                <div className="space-y-2" data-id="element-489">
                  <label className="text-xs uppercase tracking-widest text-charcoal/40 font-medium" data-id="element-490">
                    Last Name
                  </label>
                  <input type="text" className="w-full bg-white border border-warmGray/20 rounded-xl p-4 focus:ring-2 focus:ring-warmCoral/20 focus:border-warmCoral outline-none transition-all" placeholder="Doe" data-id="element-491" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-492">
                <label className="text-xs uppercase tracking-widest text-charcoal/40 font-medium" data-id="element-493">
                  Email Address
                </label>
                <input type="email" className="w-full bg-white border border-warmGray/20 rounded-xl p-4 focus:ring-2 focus:ring-warmCoral/20 focus:border-warmCoral outline-none transition-all" placeholder="jane@example.com" data-id="element-494" />
              </div>
              <div className="space-y-2" data-id="element-495">
                <label className="text-xs uppercase tracking-widest text-charcoal/40 font-medium" data-id="element-496">
                  Phone Number
                </label>
                <input type="tel" className="w-full bg-white border border-warmGray/20 rounded-xl p-4 focus:ring-2 focus:ring-warmCoral/20 focus:border-warmCoral outline-none transition-all" placeholder="+1 (555) 000-0000" data-id="element-497" />
              </div>
              <div className="space-y-2" data-id="element-498">
                <label className="text-xs uppercase tracking-widest text-charcoal/40 font-medium" data-id="element-499">
                  How can we help?
                </label>
                <textarea rows={5} className="w-full bg-white border border-warmGray/20 rounded-xl p-4 focus:ring-2 focus:ring-warmCoral/20 focus:border-warmCoral outline-none transition-all resize-none" placeholder="Please share any specific questions or details about your needs..." data-id="element-500" />
              </div>
              <button className="w-full bg-charcoal text-white py-5 rounded-2xl text-lg font-medium hover:bg-warmCoral transition-all shadow-xl mt-4" data-id="element-501">
                Submit Request
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>;
};